import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { ChevronRight, ChevronDown, Plus, Pencil, Trash2, FolderTree, Package } from 'lucide-react';

export default function VendorInventory() {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editNode, setEditNode] = useState(null);
  const [deleteNode, setDeleteNode] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  const fetchTree = async () => {
    setLoading(true);
    try {
      const response = await api.get('/vendor/inventory/category-tree');
      setTree(response.data.tree || []);
    } catch (err) {
      toast.error('Failed to load category tree');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTree();
  }, []);

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleCreate = async () => {
    if (!categoryName.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      await api.post('/vendor/inventory/category-nodes', {
        name: categoryName,
        parent_id: selectedParent?.id || null
      });
      toast.success('Category created');
      setShowCreate(false);
      setCategoryName('');
      setSelectedParent(null);
      fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleEdit = async () => {
    if (!categoryName.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      await api.put(`/vendor/inventory/category-nodes/${editNode.id}`, {
        name: categoryName
      });
      toast.success('Category updated');
      setEditNode(null);
      setCategoryName('');
      fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update category');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/vendor/inventory/category-nodes/${deleteNode.id}`);
      toast.success('Category deleted');
      setDeleteNode(null);
      fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete category');
    }
  };

  const openCreateDialog = (parentNode = null) => {
    setSelectedParent(parentNode);
    setCategoryName('');
    setShowCreate(true);
  };

  const openEditDialog = (node) => {
    setEditNode(node);
    setCategoryName(node.name);
  };

  const renderTreeNode = (node, depth = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);

    return (
      <div key={node.id} className="select-none">
        <div 
          className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-accent/50 group"
          style={{ paddingLeft: `${depth * 20 + 12}px` }}
        >
          {hasChildren ? (
            <button onClick={() => toggleNode(node.id)} className="shrink-0">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          ) : (
            <div className="w-4" />
          )}
          
          <FolderTree className="h-4 w-4 text-primary shrink-0" />
          
          <span className="flex-1 text-sm font-medium">{node.name}</span>
          
          <Badge variant="outline" className="text-xs">
            {node.item_count || 0} items
          </Badge>

          <div className="opacity-0 group-hover:opacity-100 flex gap-1">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7"
              onClick={() => openCreateDialog(node)}
              data-testid={`add-subcategory-${node.id}`}
            >
              <Plus className="h-3 w-3" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7"
              onClick={() => openEditDialog(node)}
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7 text-destructive"
              onClick={() => setDeleteNode(node)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
        
        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">Inventory & Categories</h1>
          <p className="text-muted-foreground mt-1">Organize your menu with unlimited category levels.</p>
        </div>
        <Button onClick={() => openCreateDialog()} data-testid="create-root-category">
          <Plus className="h-4 w-4 mr-2" /> New Category
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" /> Category Tree
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-10" />)}
            </div>
          ) : tree.length > 0 ? (
            <div className="space-y-1">
              {tree.map(node => renderTreeNode(node))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FolderTree className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No categories yet. Create your first category to organize your menu.</p>
              <Button onClick={() => openCreateDialog()} className="mt-4">
                <Plus className="h-4 w-4 mr-2" /> Create Category
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedParent ? `New Subcategory under "${selectedParent.name}"` : 'New Root Category'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input 
                value={categoryName} 
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="e.g., Beverages, Pizza, Desserts"
                data-testid="category-name-input"
              />
            </div>
            {selectedParent && (
              <p className="text-sm text-muted-foreground">
                Full path: {selectedParent.path} &gt; {categoryName || '...'}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setCategoryName(''); setSelectedParent(null); }}>
              Cancel
            </Button>
            <Button onClick={handleCreate} data-testid="save-category-button">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editNode} onOpenChange={() => { setEditNode(null); setCategoryName(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input 
                value={categoryName} 
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Category name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditNode(null); setCategoryName(''); }}>
              Cancel
            </Button>
            <Button onClick={handleEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteNode} onOpenChange={() => setDeleteNode(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteNode?.name}"? This action cannot be undone.
              {deleteNode?.item_count > 0 && (
                <span className="block mt-2 text-destructive font-medium">
                  This category contains {deleteNode.item_count} items and cannot be deleted.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-destructive text-destructive-foreground"
              disabled={deleteNode?.item_count > 0}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}